import React, { useEffect, useState, useRef } from "react";
import { useSeo } from "@/lib/seo";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatError, fileUrl } from "@/lib/api";
import ImageThumb from "@/components/ImageThumb";
import AuthBadge, { authState } from "@/components/AuthBadge";
import PhotoUpload from "@/components/PhotoUpload";
import CategoryName from "@/components/CategoryName";
import { RepRow, SentimentTag } from "@/components/SellerRep";
import BumpTierPicker from "@/components/BumpTierPicker";
import { Heart, ShoppingBag, Flag, ShieldCheck, AlertTriangle, X, TrendingUp, Tag, ChevronLeft, BadgeCheck, Ruler, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ERA_LABELS, REPORT_REASONS, GRADE_LABELS, measurementSpec, FABRIC_TYPE_LABELS, JEWELRY_HALLMARK_LABELS as HALLMARK_LABELS, ACCESSORY_STYLE_LABELS, JEWELRY_METAL_LABELS, WATCH_MOVEMENT_LABELS, WATCH_BAND_LABELS } from "@/lib/taxonomy";
import { pushRecentlyViewed } from "@/lib/recentlyViewed";
import RecentlyViewed from "@/components/RecentlyViewed";
import BrandFollowButton from "@/components/BrandFollowButton";

function categoryCrumb(item) {
  const pt = item.product_type;
  if (pt === "apparel") {
    const kid = ["infant", "toddler", "kid", "teen"].includes(item.age_group);
    if (kid) return { to: "/browse?product_type=apparel&dept=kids", label: "Kids" };
    return item.gender === "men"
      ? { to: "/browse?product_type=apparel&dept=men", label: "Men" }
      : { to: "/browse?product_type=apparel&dept=women", label: "Women" };
  }
  if (pt === "fabric" || item.category === "fabric_archives") return { to: "/browse?product_type=fabric", label: "Fabrics" };
  if (pt) return { to: `/browse?product_type=${pt}`, label: pt.charAt(0).toUpperCase() + pt.slice(1) };
  return { to: "/browse", label: "Browse" };
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [active, setActive] = useState(0);
  const [likeColor, setLikeColor] = useState(true);
  const [likePattern, setLikePattern] = useState(true);
  const [likeStyle, setLikeStyle] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState({ reason: REPORT_REASONS[0], note: "", evidence_paths: [] });
  const [sending, setSending] = useState(false);
  const [sellerRating, setSellerRating] = useState(null);
  const [boostTiers, setBoostTiers] = useState(null);
  const viewedRef = useRef(null);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerPrice, setOfferPrice] = useState("");
  const [offerBusy, setOfferBusy] = useState(false);
  const [tradeable, setTradeable] = useState(false);
  const [tradeBusy, setTradeBusy] = useState(false);
  useEffect(() => { setTradeable(!!item?.tradeable); }, [item]);

  const toggleTrade = async () => {
    setTradeBusy(true);
    try {
      const { data } = await api.patch(`/listings/${item.id}/tradeable`);
      setTradeable(data.tradeable);
      toast.success(data.tradeable ? "Trades turned on for this item" : "Trades turned off");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setTradeBusy(false); }
  };
  const proposeTrade = async () => {
    if (!user) { nav("/auth"); return; }
    setTradeBusy(true);
    try {
      await api.post("/trades", { target_listing_id: item.id });
      toast.success("Trade request sent! The seller will browse your items.");
      nav("/trades");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setTradeBusy(false); }
  };

  const makeOffer = async () => {
    const val = parseFloat(offerPrice);
    if (!val || val <= 0 || val >= item.price) { toast.error(`Enter an amount below $${item.price}`); return; }
    setOfferBusy(true);
    try {
      await api.post("/offers", { listing_id: item.id, offer_price: val });
      toast.success("Offer sent! The seller has 48 hours to accept, decline, or counter.");
      setOfferOpen(false); setOfferPrice("");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setOfferBusy(false); }
  };

  const load = () => api.get(`/listings/${id}`).then((r) => setItem(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (id && viewedRef.current !== id) {
      viewedRef.current = id;
      pushRecentlyViewed(id);
      window.scrollTo(0, 0);
      api.post(`/listings/${id}/view`).catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    if (!item?.seller_id) return;
    api.get(`/sellers/${item.seller_id}/reviews`).then((r) => setSellerRating(r.data)).catch(() => {});
  }, [item?.seller_id]);

  useEffect(() => {
    api.get("/pricing").then((r) => setBoostTiers(r.data?.boost?.home_bump_tiers)).catch(() => {});
  }, []);

  useEffect(() => {
    if (item?.auth_status !== "screening") return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [item?.auth_status, id]);

  useSeo(item ? {
    title: `${item.title}${item.brand ? " · " + item.brand : ""} — Your Classic Style`,
    description: item.description || `Shop ${item.title}${item.brand ? " by " + item.brand : ""} on Your Classic Style — a curated vintage & classic marketplace.`,
    image: (item.image_paths && item.image_paths[0]) ? (item.image_paths[0].startsWith("http") ? item.image_paths[0] : fileUrl(item.image_paths[0])) : undefined,
    jsonLd: {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: item.title,
      image: (item.image_paths || []).map((p) => (p.startsWith("http") ? p : fileUrl(p))),
      description: (item.description || `${item.title} on Your Classic Style`).slice(0, 500),
      ...(item.brand ? { brand: { "@type": "Brand", name: item.brand } } : {}),
      offers: {
        "@type": "Offer",
        priceCurrency: "USD",
        price: Number(item.price || 0).toFixed(2),
        availability: item.sold ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
        itemCondition: /new/i.test(item.condition || "") ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition",
        url: typeof window !== "undefined" ? window.location.href : undefined,
      },
    },
  } : {});

  if (!item) return <div className="p-16 label-eyebrow">Loading…</div>;

  const state = authState(item);
  const mine = user && user.id === item.seller_id;
  const isFabric = item.category === "fabric_archives";
  const reduced = item.is_reduced && item.original_price > item.price;
  const era = item.era && item.era !== "unspecified" ? ERA_LABELS[item.era] : null;
  const rep = item.auth_report || {};
  const blocked = item.demo_sold_out ? "Sold out — fresh listings from real sellers coming soon"
    : item.sold ? "Sold — this piece has found its owner"
    : item.auth_status === "screening" ? "On hold while our AI screens the authenticity proof"
    : item.auth_status === "rejected" ? "Delisted — this listing failed authenticity screening"
    : item.active === false ? "No longer available"
    : null;

  const like = async () => {
    if (!user) { nav("/auth"); return; }
    try {
      const { data } = await api.post(`/likes/${id}`, {
        color: likeColor ? item.color : null,
        pattern: likePattern ? item.pattern : null,
        style: likeStyle ? item.style : null,
      });
      toast.success(data.liked ? "Saved to your likes" : "Removed from likes");
    } catch { toast.error("Could not save"); }
  };

  const addCart = async () => {
    if (!user) { nav("/auth"); return; }
    try { await api.post(`/cart/${id}`); toast.success("Added to bag"); }
    catch { toast.error("Could not add"); }
  };

  const submitReport = async () => {
    if (!user) { nav("/auth"); return; }
    setSending(true);
    try {
      await api.post("/reports", { listing_id: id, ...report });
      toast.success("Report filed — our AI is reviewing this listing now");
      setReportOpen(false);
      setReport({ reason: REPORT_REASONS[0], note: "", evidence_paths: [] });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="product-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 grid lg:grid-cols-2 gap-12">
      <button
        type="button"
        data-testid="product-back-btn"
        onClick={() => (window.history.length > 1 ? nav(-1) : nav("/browse"))}
        className="lg:col-span-2 -mt-2 mb-1 inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-black transition-colors w-fit"
      >
        <ChevronLeft size={16} /> Back
      </button>
      {(() => {
        const c = categoryCrumb(item);
        let backTo = c.to;
        try { backTo = sessionStorage.getItem("ycs_last_browse") || c.to; } catch (e) { console.debug("sessionStorage read failed", e); }
        return (
          <nav data-testid="product-breadcrumb" className="lg:col-span-2 flex items-center gap-2 text-xs text-neutral-500 mb-1 flex-wrap">
            <Link to="/" className="hover:text-black">Home</Link>
            <span className="text-neutral-300">›</span>
            <Link to={backTo} data-testid="breadcrumb-category" className="hover:text-black">{c.label}</Link>
            <span className="text-neutral-300">›</span>
            <span className="text-neutral-800 truncate max-w-[240px]" data-testid="breadcrumb-current">{item.title}</span>
          </nav>
        );
      })()}
      <div>
        <div className="aspect-[4/5] bg-neutral-50 hairline overflow-hidden">
          <ImageThumb path={item.image_paths?.[active]} alt={item.title} className="w-full h-full object-cover" />
        </div>
        {item.image_paths?.length > 1 && (
          <div className="mt-3 grid grid-cols-5 gap-2">
            {item.image_paths.map((p, i) => (
              <button key={`${p}-${i}`} onClick={() => setActive(i)} className={`aspect-square hairline ${i === active ? "outline outline-2 outline-black" : ""}`} data-testid={`thumb-${i}`}>
                <ImageThumb path={p} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
        {item.proof_paths?.length > 0 && (
          <div className="mt-6 hairline p-5" data-testid="proof-gallery">
            <div className="flex items-center gap-2">
              <ShieldCheck size={14} style={{ color: "#16A34A" }} />
              <span className="label-eyebrow">Authenticity proof from the seller</span>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {item.proof_paths.map((p, i) => (
                <div key={p} className="aspect-square hairline overflow-hidden" data-testid={`proof-img-${i}`}>
                  <ImageThumb path={p} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}
        {item.label_paths?.length > 0 && (
          <div className="mt-6 hairline p-5" data-testid="label-gallery">
            <span className="label-eyebrow">Brand / size / care label</span>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {item.label_paths.map((p, i) => (
                <div key={p} className="aspect-square hairline overflow-hidden" data-testid={`label-img-${i}`}>
                  <ImageThumb path={p} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}
        {item.flaw_paths?.length > 0 && (
          <div className="mt-6 hairline p-5" data-testid="flaw-gallery" style={{ borderColor: "#EA580C" }}>
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} style={{ color: "#EA580C" }} />
              <span className="label-eyebrow" style={{ color: "#EA580C" }}>Flaw close-ups from the seller</span>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {item.flaw_paths.map((p, i) => (
                <div key={p} className="aspect-square hairline overflow-hidden" data-testid={`flaw-img-${i}`}>
                  <ImageThumb path={p} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-2">
          <AuthBadge item={item} testid="detail-auth-badge" />
          {era && <span className="chip" data-testid="detail-era-chip">{era}</span>}
          {item.brand && <span className="chip" data-active="true" data-testid="detail-brand-chip">{item.brand}</span>}
          {item.brand && <BrandFollowButton brand={item.brand} size={12} className="text-[0.7rem]" />}
        </div>
        <div className="label-eyebrow mt-4">
          {isFabric
            ? <><CategoryName /> · {FABRIC_TYPE_LABELS[item.product_type] || item.product_type}</>
            : `${item.gender} · ${item.age_group} · ${item.product_type}`}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-3">{item.title}</h1>
        <div className="mt-4 flex items-baseline gap-3 flex-wrap">
          {reduced && <span className="text-lg line-through text-neutral-400">${Number(item.original_price).toFixed(2)}</span>}
          <span className="font-display text-3xl" style={reduced ? { color: "#2563EB" } : {}}>${Number(item.price).toFixed(2)}</span>
          {reduced && <span className="chip" data-active="true">Reduced by seller</span>}
          {item.condition_grade
            ? <span className="chip" data-active="true" data-testid="detail-grade-chip">{GRADE_LABELS[item.condition_grade] || item.condition_grade}</span>
            : item.condition && <span className="chip" data-active={item.condition === "new"}>{item.condition === "new" ? "New" : "Pre-loved"}</span>}
          {item.hallmark && item.hallmark !== "other" && (
            <span className="chip inline-flex items-center gap-1" data-testid="detail-hallmark-badge"
              style={{ borderColor: "#C99A2E", color: "#8A6A15", background: "#FBF6E9" }}>
              <BadgeCheck size={12} style={{ color: "#C99A2E" }} /> {HALLMARK_LABELS[item.hallmark] || item.hallmark}
            </span>
          )}
          {item.resizable && <span className="chip" data-testid="detail-resizable-badge"><Ruler size={12} /> Resizable</span>}
          {mine ? (
            <button onClick={toggleTrade} disabled={tradeBusy} data-testid="trade-toggle-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] rounded-full transition-colors whitespace-nowrap"
              style={{ background: tradeable ? "#16A34A" : "#DC2626", color: "#fff" }}>
              <ArrowLeftRight size={12} /> Trade: {tradeable ? "On" : "Off"}
            </button>
          ) : tradeable && !item.sold ? (
            <button onClick={proposeTrade} disabled={tradeBusy} data-testid="propose-trade-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] rounded-full transition-colors whitespace-nowrap"
              style={{ background: "#16A34A", color: "#fff" }}>
              <ArrowLeftRight size={12} /> Propose a trade
            </button>
          ) : null}
          <button
            data-testid="sell-one-like-this-btn"
            onClick={() => nav("/sell", { state: { prefill: {
              category: item.category, product_type: item.product_type,
              gender: item.gender, age_group: item.age_group, kid_gender: item.kid_gender,
              garment_type: item.garment_type, color: item.color, pattern: item.pattern,
              style: item.style, material: item.material, size: item.size,
              era: item.era, waist: item.waist, inseam: item.inseam,
              band: item.band, movement: item.movement, dial_color: item.dial_color,
              metal: item.metal, stone: item.stone, water_resistance: item.water_resistance,
              resizable: item.resizable, hallmark: item.hallmark,
            } } })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 hairline text-[0.65rem] uppercase tracking-[0.14em] hover:bg-black/5 transition-colors whitespace-nowrap"
            style={{ borderColor: "#1E3A2B", color: "#1E3A2B" }}>
            <Tag size={12} /> Sell one like this
          </button>
        </div>
        {["watches", "rings", "necklaces", "bracelets", "earrings", "wallets", "hats", "socks", "ties", "handbags"].includes(item.product_type) && (() => {
          const rows = [
            ["Style", ACCESSORY_STYLE_LABELS[item.garment_type] || ""],
            ["Band", WATCH_BAND_LABELS[item.band] || item.band],
            ["Movement", WATCH_MOVEMENT_LABELS[item.movement] || item.movement],
            ["Dial colour", item.dial_color],
            ["Water resistance", item.water_resistance],
            ["Metal", JEWELRY_METAL_LABELS[item.metal] || item.metal],
            ["Stone", item.stone && item.stone !== "none" ? item.stone : ""],
            ["Hallmark", item.hallmark && item.hallmark !== "other" ? (HALLMARK_LABELS[item.hallmark] || item.hallmark) : ""],
            ["Card slots", item.card_slots],
            ["Resizable", item.resizable ? "Yes" : ""],
          ].filter(([, v]) => v);
          if (!rows.length) return null;
          return (
            <div className="mt-6 hairline p-5" data-testid="accessory-spec-panel">
              <div className="label-eyebrow mb-3">Specifications</div>
              <div className="grid grid-cols-2 gap-y-2 gap-x-6 text-sm">
                {rows.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-neutral-100 pb-2">
                    <span className="text-neutral-500">{k}</span>
                    <span className="text-right capitalize font-medium">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        {item.seller_id && (
          <Link to={`/seller/${item.seller_id}`} className="mt-3 inline-flex items-center gap-2 group" data-testid="detail-seller-link">
            <span className="text-sm text-neutral-600">Sold by <span className="underline group-hover:text-black">{item.seller_name || "this seller"}</span></span>
            {sellerRating && (sellerRating.count > 0 || sellerRating.verified_sales > 0) && (
              <span className="flex items-center gap-1.5" data-testid="detail-seller-rating">
                <RepRow good={sellerRating.good} bad={sellerRating.bad} verified={sellerRating.verified_sales} size={13} />
              </span>
            )}
          </Link>
        )}
        <p className="mt-6 text-neutral-600 leading-relaxed">{item.description || "No description provided."}</p>

        {mine && !item.sold && item.active !== false && (() => {
          const boosted = item.bump_until && item.bump_until > new Date().toISOString();
          const fmt = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
          return (
            <div className="hairline mt-6 p-5" data-testid="bump-panel" style={{ borderColor: "#2563EB" }}>
              <div className="flex items-center gap-2">
                <TrendingUp size={15} style={{ color: "#2563EB" }} />
                <span className="label-eyebrow" style={{ color: "#2563EB" }}>Boost visibility</span>
              </div>
              {boosted ? (
                <p className="mt-2 text-sm text-neutral-600" data-testid="bump-active">
                  Featured on the homepage until {fmt(item.bump_until)}.
                </p>
              ) : (
                <>
                  <p className="mt-2 mb-3 text-sm text-neutral-600">Feature this item on the homepage “For Sale” grid — pick how long:</p>
                  <BumpTierPicker listingId={item.id} tiers={boostTiers} testidPrefix="bump" />
                  <p className="mt-2 text-xs text-neutral-400">Secure Stripe checkout.</p>
                </>
              )}
            </div>
          );
        })()}

        <div className="hairline mt-8 p-6" data-testid="authenticity-panel" style={{ borderColor: state.color }}>
          <div className="flex items-center gap-2">
            <state.Icon size={15} style={{ color: state.color }} className={state.key === "screening" ? "animate-spin" : ""} />
            <span className="label-eyebrow" style={{ color: state.color }}>{state.label}</span>
          </div>
          {state.key === "verified" && (
            item.auth_score ? (
              <p className="mt-2 text-sm text-neutral-600">
                Our authentication check passed this listing at {Math.round(item.auth_score)}% confidence
                against {item.brand} brand markers.
              </p>
            ) : (
              <p className="mt-2 text-sm text-neutral-600">
                The seller guarantees this is an authentic {item.brand} original. Covered by buyer protection if it
                arrives not as described.
              </p>
            )
          )}
          {state.key === "screening" && (
            <p className="mt-2 text-sm text-neutral-600">The seller's proof photos are being screened right now. This page updates itself.</p>
          )}
          {state.key === "replica" && (
            <p className="mt-2 text-sm text-neutral-600">
              The seller disclosed this as a reproduction resembling {item.brand}. It is not an authentic original.
            </p>
          )}
          {state.key === "unbranded" && (
            <p className="mt-2 text-sm text-neutral-600">No brand is claimed on this piece, so there is nothing to fake.</p>
          )}
          {(state.key === "flagged" || state.key === "rejected") && (
            <p className="mt-2 text-sm text-neutral-600">
              {state.key === "flagged"
                ? "Our AI could not confirm this brand from the seller's proof photos, so it carries no verified badge. "
                : "This listing failed authenticity screening and has been pulled. "}
              {rep.notes || rep.reasoning || ""}
            </p>
          )}
          {rep.red_flags?.length > 0 && (mine || state.key === "rejected") && (
            <ul className="mt-3 text-xs text-neutral-500 space-y-1" data-testid="auth-red-flags">
              {rep.red_flags.slice(0, 4).map((f, i) => <li key={`${f}-${i}`} className="flex gap-2"><AlertTriangle size={12} className="mt-0.5 shrink-0" style={{ color: "#EA580C" }} />{f}</li>)}
            </ul>
          )}
          <div className="mt-4 pt-4 border-t border-black/10 text-xs text-neutral-500 leading-relaxed">
            <strong className="text-black">3-day authenticity guarantee.</strong> The seller's payout is held for 3 days
            after delivery. Report a fake from your Purchases page and our AI reviews the evidence — approved claims are
            refunded and the listing is pulled.
          </div>
        </div>

        <div className="hairline mt-4 p-6">
          <div className="label-eyebrow">Details</div>
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {item.brand && (<><dt className="text-neutral-500">Brand</dt><dd>{item.brand}</dd></>)}
            <dt className="text-neutral-500">Era Style</dt><dd data-testid="detail-era-style">{(item.era && item.era !== "unspecified") ? (ERA_LABELS[item.era] || item.era) : "Not specified"}</dd>
            <dt className="text-neutral-500">Color</dt><dd>{item.color}</dd>
            <dt className="text-neutral-500">Pattern</dt><dd>{item.pattern}</dd>
            {!isFabric && item.style && (<><dt className="text-neutral-500">Style</dt><dd>{item.style}</dd></>)}
            {item.size && (<><dt className="text-neutral-500">Size</dt><dd>{item.size}</dd></>)}
            {(item.waist || item.inseam) && (<><dt className="text-neutral-500">Waist / length</dt><dd data-testid="detail-waist-length">{[item.waist && `W${item.waist}`, item.inseam && `L${item.inseam}`].filter(Boolean).join(" × ")}</dd></>)}
            <dt className="text-neutral-500">Seller</dt><dd>{item.seller_name || "—"}</dd>
          </dl>
        </div>

        {item.condition_grade && (() => {
          const labels = Object.fromEntries(measurementSpec(item.product_type).map(([k, l]) => [k, l]));
          const measures = Object.entries(item.measurements || {}).filter(([, v]) => v && String(v).trim());
          const disclosures = [
            { ok: item.smoke_free, on: "Smoke-free home", off: "Smoke-free home not confirmed" },
            { ok: item.pet_free, on: "Pet-free home", off: "Pet-free home not confirmed" },
            { ok: item.odor_free, on: "No odor — freshly cleaned", off: "Odor not confirmed" },
          ];
          return (
            <div className="hairline mt-4 p-6" data-testid="condition-panel">
              <div className="label-eyebrow">Condition &amp; disclosures</div>
              <div className="mt-3 flex items-center gap-2">
                <span className="chip" data-active="true" data-testid="condition-grade">{GRADE_LABELS[item.condition_grade] || item.condition_grade}</span>
                {item.no_flaws
                  ? <span className="text-xs" style={{ color: "#16A34A" }} data-testid="no-flaws-note">Seller reports no visible flaws</span>
                  : (item.flaw_paths?.length > 0 && <span className="text-xs text-neutral-500">Flaws pictured above</span>)}
              </div>

              {measures.length > 0 && (
                <div className="mt-5" data-testid="measurements-panel">
                  <div className="text-xs uppercase tracking-[0.16em] text-neutral-500 mb-2">Measurements</div>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    {measures.map(([k, v]) => (
                      <React.Fragment key={k}>
                        <dt className="text-neutral-500">{labels[k] || k}</dt><dd>{v}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </div>
              )}

              <ul className="mt-5 space-y-1.5 text-sm" data-testid="disclosures-panel">
                {disclosures.map((d) => (
                  <li key={d.on} className="flex items-center gap-2">
                    {d.ok
                      ? <ShieldCheck size={13} style={{ color: "#16A34A" }} />
                      : <span className="w-[13px] h-[13px] inline-block" />}
                    <span className={d.ok ? "" : "text-neutral-400"}>{d.ok ? d.on : d.off}</span>
                  </li>
                ))}
                {item.altered && (
                  <li className="flex items-start gap-2" data-testid="alteration-note">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" style={{ color: "#EA580C" }} />
                    <span>Altered / repaired{item.alteration_note ? `: ${item.alteration_note}` : ""}</span>
                  </li>
                )}
              </ul>
            </div>
          );
        })()}

        {sellerRating && (
          <div className="hairline mt-4 p-6" data-testid="seller-reviews">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">Seller reviews</div>
              {(sellerRating.count > 0 || sellerRating.verified_sales > 0) && (
                <div className="flex items-center gap-2" data-testid="seller-rating-summary">
                  <RepRow good={sellerRating.good} bad={sellerRating.bad} verified={sellerRating.verified_sales} />
                </div>
              )}
            </div>
            {sellerRating.count === 0 ? (
              <p className="mt-3 text-sm text-neutral-500">No reviews yet — be the first once your order arrives.</p>
            ) : (
              <ul className="mt-4 space-y-4">
                {sellerRating.reviews.slice(0, 4).map((rv, i) => (
                  <li key={`${rv.created_at || "rv"}-${rv.buyer_name || i}`} className="hairline-b pb-4 last:border-0 last:pb-0" data-testid={`seller-review-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-display text-sm">{rv.buyer_name}</span>
                      <SentimentTag sentiment={rv.sentiment} size={12} />
                    </div>
                    {rv.comment && <p className="mt-1 text-sm text-neutral-600 leading-relaxed">{rv.comment}</p>}
                    {rv.item_title && <p className="mt-1 text-[0.62rem] uppercase tracking-[0.14em] text-neutral-400">on {rv.item_title}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="hairline mt-4 p-6">
          <div className="label-eyebrow">Save by criteria</div>
          <p className="text-xs text-neutral-500 mt-2">Choose what you like about this piece. We'll surface matching new listings for you.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button className="chip" data-active={likeColor} onClick={() => setLikeColor((v) => !v)} data-testid="like-color-chip">Color: {item.color}</button>
            <button className="chip" data-active={likePattern} onClick={() => setLikePattern((v) => !v)} data-testid="like-pattern-chip">Pattern: {item.pattern}</button>
            {!isFabric && item.style && <button className="chip" data-active={likeStyle} onClick={() => setLikeStyle((v) => !v)} data-testid="like-style-chip">Style: {item.style}</button>}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 items-center">
          {blocked ? (
            <div className="hairline px-4 py-3 text-xs uppercase tracking-[0.16em] w-full sm:w-auto"
              data-testid="purchase-blocked" style={{ color: "#E11D48", borderColor: "#E11D48" }}>
              {blocked}
            </div>
          ) : (
            <button onClick={addCart} data-testid="add-to-bag-btn" className="btn-primary"><ShoppingBag size={14} /> Add to bag</button>
          )}
          <button onClick={like} data-testid="save-item-btn" className="btn-outline"><Heart size={14} /> Save</button>
          {!mine && item.active !== false && !item.sold && !item.demo_sold_out && (
            <button onClick={() => (user ? setOfferOpen(true) : nav("/auth"))} data-testid="make-offer-btn" className="btn-outline">
              <Tag size={14} /> Make an offer
            </button>
          )}
          {!mine && (
            <button onClick={() => (user ? setReportOpen(true) : nav("/auth"))} data-testid="report-btn"
              className="ml-auto inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-black transition-colors">
              <Flag size={13} /> Report a fake
            </button>
          )}
        </div>
      </div>

      {offerOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOfferOpen(false)}>
          <div className="bg-white hairline p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="offer-modal">
            <div className="flex items-start justify-between">
              <div>
                <div className="label-eyebrow">Make an offer</div>
                <h2 className="font-display text-2xl mt-1">{item.title}</h2>
              </div>
              <button onClick={() => setOfferOpen(false)} className="p-1" data-testid="offer-close"><X size={16} /></button>
            </div>
            <p className="mt-3 text-xs text-neutral-500 leading-relaxed">
              Asking price ${item.price}. Offer a fair price below it — the seller can accept, decline, or counter within 48 hours.
            </p>
            <div className="mt-5">
              <div className="label-eyebrow mb-2">Your offer (USD)</div>
              <input type="number" min="1" max={item.price - 1} value={offerPrice}
                onChange={(e) => setOfferPrice(e.target.value)} data-testid="offer-input"
                placeholder={`e.g. ${Math.max(1, Math.floor(item.price * 0.8))}`}
                className="w-full hairline p-3 text-sm" />
            </div>
            <button onClick={makeOffer} disabled={offerBusy} data-testid="offer-submit" className="btn-primary w-full mt-5">
              {offerBusy ? "Sending…" : "Send offer"}
            </button>
          </div>
        </div>
      )}

      {reportOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setReportOpen(false)}>
          <div className="bg-white hairline p-8 w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="report-modal">
            <div className="flex items-start justify-between">
              <div>
                <div className="label-eyebrow">Report a counterfeit</div>
                <h2 className="font-display text-2xl mt-1">{item.title}</h2>
              </div>
              <button onClick={() => setReportOpen(false)} className="p-1" data-testid="report-close"><X size={16} /></button>
            </div>
            <p className="mt-3 text-xs text-neutral-500 leading-relaxed">
              Our AI re-inspects the listing photos against your evidence. Upheld reports delist the item and give the
              seller a strike — three strikes suspends the account. False reports are dismissed automatically.
            </p>
            <div className="mt-5">
              <div className="label-eyebrow mb-2">What looks wrong?</div>
              <select className="w-full hairline p-3 text-sm bg-white" data-testid="report-reason"
                value={report.reason} onChange={(e) => setReport((r) => ({ ...r, reason: e.target.value }))}>
                {REPORT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="mt-4">
              <div className="label-eyebrow mb-2">Details</div>
              <textarea rows={3} className="w-full hairline p-3 text-sm" data-testid="report-note"
                placeholder="Tell us exactly what you noticed" value={report.note}
                onChange={(e) => setReport((r) => ({ ...r, note: e.target.value }))} />
            </div>
            <div className="mt-4">
              <div className="label-eyebrow mb-2">Evidence photos (optional)</div>
              <PhotoUpload paths={report.evidence_paths} testid="report-evidence"
                onChange={(p) => setReport((r) => ({ ...r, evidence_paths: p }))} cta="Add evidence" />
            </div>
            <button disabled={sending} onClick={submitReport} className="btn-primary w-full mt-6" data-testid="report-submit">
              {sending ? "Filing…" : "Submit report"}
            </button>
          </div>
        </div>
      )}
      <RecentlyViewed excludeId={id} className="!px-0 !max-w-none lg:col-span-2 mt-6" />
    </div>
  );
}
